// Waiting on an Ask Mira BI turn, and reading the chart it produced.
//
// `POST /api/bi/ask/threads` (and `.../messages`) answer 202 with a thread id
// and the position of the PENDING assistant message: the turn is a model loop
// with up to three BigQuery scans, so it never runs on a web request. The
// client waits with a Pusher fast path over a REST poll, whichever lands first.
//
//   1. `useAskThread(threadId)`: the conversation (`bi.ask.progress` while a
//      turn works, `bi.ask.settled` when it ends, then
//      `GET /api/bi/ask/threads/{threadId}`). The pending message carries the
//      steps so far, so the reader watches the agent work. The poll parks
//      when no message is `pending`.
//   2. `useAskChartResult(resultKey)`: one settled message's rows
//      (`GET /api/bi/results/{resultKey}`). One fetch, no wait (see its doc).
//
// The poll reads Postgres, not the warehouse, so it costs no query and still
// resolves the page when Pusher is broken. It is not the primary mechanism.
import { useCallback, useEffect, useRef, useState } from "react";

import { usePusherChannel } from "@bi/shared";
import type { AskMessageWire, AskThreadView, BiAskProgress, BiAskSettled } from "@bi/shared/types";

import { biChannel, biPusherConfig } from "../config";
import type { ChartRenderState } from "./bi-query";
import { ApiError, fetchAskThread, fetchChartResult } from "./api";

/** Both Ask pushes re-read the thread: a step moved, or the turn settled.
 *  Module-level so the subscription's event list is one stable array. */
const ASK_EVENTS = ["bi.ask.settled", "bi.ask.progress"] as const;

/** How often a thread with a turn in flight re-reads over REST. */
const POLL_MS = 2_000;

/** How long to follow a turn before giving up on it.
 *
 *  Must equal the server's `PENDING_TURN_STALE_SECONDS`
 *  (`bi/repo/bi_ask_threads.py`): this decides when the reader is told to
 *  retry, that decides when the route accepts a retry. It is the ceiling for
 *  a broken Pusher, not a target. */
const PENDING_TURN_STALE_MS = 300_000;

/** The server measures staleness from the message row's `created_at`; this
 *  clock starts on the last settled POLL TICK, up to `POLL_MS` before the POST
 *  that wrote that row. Without the margin the reader is told to retry a
 *  couple of seconds before `has_pending_turn` will accept one, and gets "that
 *  conversation is still answering the previous question" - the exact thing
 *  the lockstep exists to prevent. Two ticks, so a slow round trip does not
 *  eat it. */
const POLL_DEADLINE_MS = PENDING_TURN_STALE_MS + 2 * POLL_MS;

/** Whether this conversation has stopped moving.
 *
 *  Reads only the LAST message, as the server does: one turn at a time per
 *  thread, and a `pending` row older than `PENDING_TURN_STALE_SECONDS` no
 *  longer counts. An abandoned pending row (a lost turn, or the one the
 *  `bi_ask_enabled`-off path leaves) is never settled, so asking "is ANY
 *  message pending?" would disable the composer and poll forever. */
export function isThreadSettled(view: AskThreadView, now: number = Date.now()): boolean {
  const last = view.messages[view.messages.length - 1];
  return !last || !isAwaitingAnswer(last, now);
}

/** Whether this message is a turn still worth waiting on, as opposed to one
 *  that was lost.
 *
 *  The age check mirrors the server's `PENDING_TURN_STALE_SECONDS`. A dropped
 *  turn (e.g. `BI_ASK_ENABLED` on web but not on the worker) is never settled,
 *  so without it every visit starts a fresh five-minute spinner on a dead turn. */
export function isAwaitingAnswer(message: AskMessageWire, now: number = Date.now()): boolean {
  if (message.status !== "pending") return false;
  const startedAt = Date.parse(message.createdAt);
  if (Number.isNaN(startedAt)) return true; // unreadable timestamp: wait, don't guess
  return now - startedAt < PENDING_TURN_STALE_MS;
}

/** Whether the composer should be disabled: a turn is in flight AND we are
 *  still following it.
 *
 *  After the poll deadline sets `error`, polling stops and a pending message
 *  never changes in this tab, so the composer must re-enable under "try asking
 *  again" or the page is dead. */
export function isWaiting(view: AskThreadView | null, error: string | null): boolean {
  return error === null && view !== null && !isThreadSettled(view);
}

/** The message a turn is waiting on, or null when nothing is in flight. */
export function pendingMessage(view: AskThreadView | null): AskMessageWire | null {
  return view?.messages.find((m) => m.status === "pending") ?? null;
}

/** A question the reader sent that the thread read does not show yet.
 *
 *  The POST returns in a few hundred milliseconds and the next thread read
 *  lands after it, so without this the question the reader just typed
 *  vanishes from the composer and reappears a beat later. Chat readers take
 *  that gap as "did it send?". `threadId` is null until the server names the
 *  thread (a first question), and `seq` is null until the 202 says which
 *  assistant row will answer. */
export interface SentQuestion {
  threadId: string | null;
  seq: number | null;
  question: string;
  sentAt: string;
}

/** The transcript to render: the thread as read, plus the question just sent
 *  and a pending answer under it, until the read catches up.
 *
 *  "Caught up" is the read holding the assistant row the 202 named (or any
 *  later one). Before the 202, nothing on the server can be it, so the
 *  question always shows. The two placeholder rows take the next two seqs, so
 *  they sort after the transcript and are replaced by the real rows, not
 *  duplicated, once those land. Pure and exported so the rule carries a test. */
export function withSentQuestion(
  messages: AskMessageWire[],
  sent: SentQuestion | null,
): AskMessageWire[] {
  if (!sent) return messages;
  const landed = sent.seq;
  if (landed !== null && messages.some((m) => m.seq >= landed)) return messages;
  const last = messages[messages.length - 1]?.seq ?? 0;
  const base = {
    steps: [],
    chart: null,
    resultKey: "",
    sourceId: null,
    bytesEstimate: null,
    failureKind: null,
    error: null,
    createdAt: sent.sentAt,
  } satisfies Partial<AskMessageWire>;
  return [
    ...messages,
    { ...base, seq: last + 1, role: "user", status: "ok", text: sent.question },
    { ...base, seq: last + 2, role: "assistant", status: "pending", text: "" },
  ];
}

/** Whether the read now holds the question that was sent: the moment the
 *  placeholder rows can be dropped for good. */
export function sentQuestionLanded(view: AskThreadView | null, sent: SentQuestion): boolean {
  const landed = sent.seq;
  return landed !== null && view !== null && view.messages.some((m) => m.seq >= landed);
}

export interface AskThreadState {
  /** The conversation as last read. `null` until the first read lands. */
  view: AskThreadView | null;
  /** True while a turn is in flight AND we are still following it — what the
   *  composer disables on. See :func:`isWaiting`. */
  waiting: boolean;
  /** Set when a turn never settled inside the deadline, or the read failed
   *  outright (a signed-out tab, a thread somebody deleted elsewhere). */
  error: string | null;
  /** Re-read now. The caller needs this after POSTing a question: the poll
   *  parks itself on a settled thread, and the new pending row is exactly what
   *  would un-park it. */
  refresh: () => void;
}

/** Follow one conversation.
 *
 *  Re-reads on every `bi.ask.progress` and `bi.ask.settled` for this thread
 *  rather than trusting the event's own fields: both events are POINTERS by
 *  design, so the read is the only thing that knows what actually happened. */
export function useAskThread(threadId: string | null, employeeEmail: string): AskThreadState {
  const [view, setView] = useState<AskThreadView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const processedCount = useRef(0);
  const settledRef = useRef(false);
  //: Whether the poll has given up and needs re-arming on the next read.
  const gaveUpRef = useRef(false);

  const channel = threadId && employeeEmail ? biChannel(employeeEmail) : null;
  // `onReconnect` re-reads: Pusher does not replay what it published while
  // the socket was down, so a settle that fell in that gap would otherwise
  // wait on the next poll tick. Passed through a ref-stable wrapper, since
  // `refresh` is declared below.
  const refreshRef = useRef<() => void>(() => undefined);
  const events = usePusherChannel<BiAskSettled | BiAskProgress>(
    channel,
    ASK_EVENTS,
    biPusherConfig(),
    () => refreshRef.current(),
  );

  const refresh = useCallback(async (): Promise<void> => {
    if (!threadId) return;
    try {
      const next = await fetchAskThread(threadId);
      setView(next);
      settledRef.current = isThreadSettled(next);
      // Clearing the error un-parks the composer AND, when the poll had given
      // up, re-arms it: the two must happen together, or the page looks alive
      // and updates from nothing. Tracked on a ref rather than read out of the
      // `setError` updater, because an updater that calls another setter is
      // not pure and React may run it twice.
      if (gaveUpRef.current) {
        gaveUpRef.current = false;
        setPollEpoch((n) => n + 1);
      }
      setError(null);
    } catch {
      // A 404 right after the 202 is a read this replica has not caught up
      // with, not a missing thread — the poll below tries again. A persistent
      // failure is caught by the deadline rather than guessed at here.
    }
  }, [threadId]);
  useEffect(() => {
    refreshRef.current = () => void refresh();
  }, [refresh]);

  // Bumped by `refresh` to re-arm the poll after the deadline cleared it. The
  // effect's other deps do not change when the reader asks again, so without
  // this the next question would start no poll and leave Pusher as the only
  // update path.
  const [pollEpoch, setPollEpoch] = useState(0);

  useEffect(() => {
    setView(null);
    setError(null);
    settledRef.current = false;
    gaveUpRef.current = false;
    if (!threadId) return;
    let cancelled = false;
    let waitingSince = Date.now();

    void refresh();
    const timer = window.setInterval(() => {
      if (cancelled) return;
      if (settledRef.current) {
        // Settled: the clock restarts, so a long conversation's fourth
        // question gets the full deadline rather than what the first three
        // left of it.
        waitingSince = Date.now();
        return;
      }
      if (Date.now() - waitingSince > POLL_DEADLINE_MS) {
        window.clearInterval(timer);
        gaveUpRef.current = true;
        setError("This is taking too long. Try asking again.");
        return;
      }
      void refresh();
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [threadId, refresh, pollEpoch]);

  // The Pusher fast path, mirroring the processed-count idiom in `bi-query.ts`.
  useEffect(() => {
    if (!threadId) {
      processedCount.current = events.length;
      return;
    }
    const fresh = events.slice(processedCount.current);
    processedCount.current = events.length;
    if (fresh.some((e) => e.thread_id === threadId)) void refresh();
  }, [events, threadId, refresh]);

  return {
    view,
    waiting: isWaiting(view, error),
    error,
    refresh: () => void refresh(),
  };
}

/** One settled message's rows.
 *
 *  A plain fetch: `run_chart` stores the result before its message settles, so
 *  a visible `resultKey` always names written rows. Results live three hours
 *  (`RESULT_TTL_SECONDS`) and threads forever, so a 404 means expiry and gets
 *  its own message. `attempt` is the retry: bump it to re-read. */
export function useAskChartResult(resultKey: string | null, attempt = 0): ChartRenderState {
  const [state, setState] = useState<ChartRenderState>({ status: "loading" });

  useEffect(() => {
    if (!resultKey) {
      setState({ status: "loading" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    fetchChartResult(resultKey)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", result: data.result });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Only a 404 is expiry, where re-asking is the fix. Any other failure is
        // worth a retry, not another turn.
        const expired = err instanceof ApiError && err.status === 404;
        setState({
          status: "error",
          message: expired
            ? "The data behind this chart has expired. Ask the question again to rerun it."
            : "That chart's data could not be loaded. Try again in a moment.",
          isTimeout: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [resultKey, attempt]);

  return state;
}
